import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, apiBase } from "../lib/api";
import { Banner, Button, Card, EmptyState, PageBody, PageHeader } from "../ui/Field";

type Asset = { id: string; folder_id: string | null; filename: string; mime: string; size: number; width: number | null; height: number | null; alt: string | null; created_at: number };
type Folder = { id: string; parent_id: string | null; name: string };

export default function Assets() {
  const { slug } = useParams();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [folder, setFolder] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [newFolder, setNewFolder] = useState("");
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    if (!slug) return;
    try {
      const [f, a] = await Promise.all([
        api.get<{ items: Folder[] }>(`/api/sites/${slug}/asset-folders`),
        api.get<{ items: Asset[] }>(`/api/sites/${slug}/assets${folder ? `?folder=${folder}` : ""}`),
      ]);
      setFolders(f.items);
      setAssets(a.items);
    } catch (e) { setErr((e as Error).message); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug, folder]);

  async function upload(files: FileList | null) {
    if (!files || !slug) return;
    setBusy(true);
    setErr(null);
    try {
      for (const f of Array.from(files)) {
        const dims = f.type.startsWith("image/") ? await readImageDimensions(f) : { width: null, height: null };
        const sign = await api.post<{ asset_id: string; upload_url: string }>(`/api/sites/${slug}/assets/sign`, {
          filename: f.name,
          mime: f.type || "application/octet-stream",
          size: f.size,
          folder_id: folder,
          width: dims.width,
          height: dims.height,
        });
        const buf = await f.arrayBuffer();
        const res = await fetch(`${apiBase}${sign.upload_url}`, {
          method: "PUT",
          credentials: "include",
          headers: { "content-type": f.type || "application/octet-stream" },
          body: buf,
        });
        if (!res.ok) throw new Error(`Upload failed: HTTP ${res.status}`);
      }
      load();
    } catch (e) { setErr((e as Error).message); }
    finally { setBusy(false); if (fileInput.current) fileInput.current.value = ""; }
  }

  async function createFolder(e: React.FormEvent) {
    e.preventDefault();
    if (!slug || !newFolder.trim()) return;
    try {
      await api.post(`/api/sites/${slug}/asset-folders`, { name: newFolder, parent_id: folder });
      setNewFolder("");
      load();
    } catch (e) { setErr((e as Error).message); }
  }

  async function setAlt(id: string, alt: string) {
    try { await api.patch(`/api/sites/${slug}/assets/${id}`, { alt }); }
    catch (e) { setErr((e as Error).message); }
  }
  async function remove(id: string) {
    if (!confirm("Delete this asset?")) return;
    try { await api.delete(`/api/sites/${slug}/assets/${id}`); load(); }
    catch (e) { setErr((e as Error).message); }
  }

  const currentFolders = folders.filter((f) => f.parent_id === folder);
  const parentTrail = (() => {
    const trail: Folder[] = [];
    let cur = folder ? folders.find((f) => f.id === folder) : null;
    while (cur) { trail.unshift(cur); cur = cur.parent_id ? folders.find((f) => f.id === cur!.parent_id) ?? null : null; }
    return trail;
  })();

  return (
    <PageBody>
      <PageHeader
        eyebrow="№ 05 · Media library"
        title={<>Assets</>}
        description="Images, documents, and other media. Folder-organized; uploads are signed and stored in R2."
        actions={
          <label className="inline-flex h-9 items-center px-4 rounded-[var(--radius-sm)] bg-[var(--color-ink)] text-[var(--color-paper)] text-[13.5px] cursor-pointer hover:bg-[var(--color-ink-2)] transition-colors">
            Upload
            <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => upload(e.target.files)} />
          </label>
        }
      />
      {err && <Banner kind="error">{err}</Banner>}
      {busy && <Banner kind="info">Uploading…</Banner>}

      <Card number="A1" title="Browser" description="Click folders to navigate. Use the breadcrumb to step back up.">
        <div className="flex items-center gap-2 mb-4 flex-wrap text-[13px]">
          <button
            onClick={() => setFolder(null)}
            className={`px-2 py-1 rounded transition-colors ${folder === null ? "text-[var(--color-ink)] font-medium" : "text-[var(--color-accent)] hover:bg-[var(--color-paper-2)]"}`}
          >
            Root
          </button>
          {parentTrail.map((f, i) => (
            <span key={f.id} className="flex items-center gap-2">
              <span className="text-[var(--color-muted)]">/</span>
              <button
                onClick={() => setFolder(f.id)}
                className={`px-2 py-1 rounded transition-colors ${i === parentTrail.length - 1 ? "text-[var(--color-ink)] font-medium" : "text-[var(--color-accent)] hover:bg-[var(--color-paper-2)]"}`}
              >
                {f.name}
              </button>
            </span>
          ))}
        </div>

        <form onSubmit={createFolder} className="flex gap-2 mb-5">
          <input
            value={newFolder}
            onChange={(e) => setNewFolder(e.target.value)}
            placeholder="New folder name"
            className="flex-1 h-9 px-3 text-[13.5px] bg-[var(--color-surface)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] focus:outline-none focus:border-[var(--color-ink)] placeholder:text-[var(--color-mute-2)]"
          />
          <Button type="submit" variant="ghost">+ Folder</Button>
        </form>

        {currentFolders.length > 0 && (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3 mb-6">
            {currentFolders.map((f) => (
              <button
                key={f.id}
                onClick={() => setFolder(f.id)}
                className="bg-[var(--color-paper-2)] border border-[var(--color-rule)] rounded-[var(--radius-sm)] p-4 text-left text-[13.5px] hover:border-[var(--color-ink)] transition-colors"
              >
                <div className="text-[var(--color-muted)] text-[10.5px] uppercase tracking-[0.08em] font-mono mb-1">Folder</div>
                <div className="text-[var(--color-ink)] truncate">{f.name}</div>
              </button>
            ))}
          </div>
        )}

        {assets.length === 0 ? (
          <EmptyState title="Empty folder." description="Upload files using the button at the top right." />
        ) : (
          <div className="grid grid-cols-[repeat(auto-fill,minmax(180px,1fr))] gap-4">
            {assets.map((a) => (
              <div key={a.id} className="border border-[var(--color-rule)] rounded-[var(--radius-sm)] p-2.5 bg-[var(--color-surface)]">
                {a.mime.startsWith("image/") ? (
                  <img
                    src={`${apiBase}/api/sites/${slug}/assets/${a.id}/raw`}
                    alt={a.alt ?? a.filename}
                    className="w-full aspect-square object-cover rounded-[3px] bg-[var(--color-paper-2)]"
                  />
                ) : (
                  <div className="w-full aspect-square bg-[var(--color-paper-2)] grid place-items-center rounded-[3px] text-[var(--color-muted)] text-[11px] font-mono uppercase tracking-[0.08em]">
                    {a.mime.split("/")[1] ?? "file"}
                  </div>
                )}
                <div className="text-[12px] mt-2 text-[var(--color-ink)] break-all leading-tight">{a.filename}</div>
                <div className="text-[10.5px] text-[var(--color-muted)] font-mono mt-0.5">
                  {prettySize(a.size)}{a.width ? ` · ${a.width}×${a.height}` : ""}
                </div>
                <input
                  placeholder="alt text"
                  defaultValue={a.alt ?? ""}
                  onBlur={(e) => { if (e.target.value !== a.alt) setAlt(a.id, e.target.value); }}
                  className="w-full mt-2 px-2 py-1 text-[12px] bg-[var(--color-paper-2)] border border-[var(--color-rule)] rounded-[3px] focus:outline-none focus:border-[var(--color-ink)] placeholder:text-[var(--color-mute-2)]"
                />
                <button
                  onClick={() => remove(a.id)}
                  className="mt-2 text-[11px] text-[var(--color-rust)] bg-transparent border-0 cursor-pointer hover:underline"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        )}
      </Card>
    </PageBody>
  );
}

function prettySize(b: number) {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

function readImageDimensions(file: File): Promise<{ width: number | null; height: number | null }> {
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => res({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => res({ width: null, height: null });
    img.src = URL.createObjectURL(file);
  });
}

